// file path: src/stores/workbot.slice.ts

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { WorkbotConversation } from '../../../services/workbot/conversation.service';

export interface WorkbotState {
  conversations: WorkbotConversation[];
  page: number;
  hasMore: boolean;
  loading: boolean;
}

const initialState: WorkbotState = {
  conversations: [],
  page: 1,
  hasMore: true,
  loading: false,
};

function mergeUniqueAndSort(
  existing: WorkbotConversation[],
  incoming: WorkbotConversation[]
): WorkbotConversation[] {
  const map = new Map<number, WorkbotConversation>();
  for (const c of existing) map.set(c.id, c);
  for (const c of incoming) map.set(c.id, c);
  return Array.from(map.values()).sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
}

const workbotSlice = createSlice({
  name: 'workbot',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setHasMore: (state, action: PayloadAction<boolean>) => {
      state.hasMore = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setConversations: (state, action: PayloadAction<WorkbotConversation[]>) => {
      state.conversations = mergeUniqueAndSort([], action.payload);
    },
    addConversations: (state, action: PayloadAction<WorkbotConversation[]>) => {
      state.conversations = mergeUniqueAndSort(state.conversations, action.payload);
    },
    prependConversation: (state, action: PayloadAction<WorkbotConversation>) => {
      state.conversations = mergeUniqueAndSort([action.payload, ...state.conversations], []);
    },
    updateConversationTitle: (
      state,
      action: PayloadAction<{ id: number; title: string }>
    ) => {
      const idx = state.conversations.findIndex(c => c.id === action.payload.id);
      if (idx >= 0) {
        state.conversations[idx] = {
          ...state.conversations[idx],
          title: action.payload.title,
        };
      }
    },
    removeConversation: (
      state,
      action: PayloadAction<number>
    ) => {
      state.conversations = state.conversations.filter(c => c.id !== action.payload);
    },
  },
});

export const {
  setLoading,
  setHasMore,
  setPage,
  setConversations,
  addConversations,
  prependConversation,
  updateConversationTitle,
  removeConversation,
} = workbotSlice.actions;

export default workbotSlice.reducer;


